/**
 * TOAST (UX-1C) — feedback LIGERO de acciones completadas.
 *
 * `ToastProvider` + `useToast()`; sin librería ni estado global nuevo.
 *   · success / info / warning / error. El error dura más (8 s frente a 4 s).
 *   · Se anuncia por una región `aria-live` que existe SIEMPRE (los lectores
 *     de pantalla no anuncian regiones que aparecen junto con su texto):
 *     `polite` para éxito/info y `assertive` para aviso/error. Sin rol
 *     status/alert: ésos quedan para los mensajes contextuales de la página.
 *   · Nunca roba el foco; se puede cerrar con su botón; como mucho 3 a la
 *     vista (el más antiguo sale primero).
 *
 * NO es para validación de formularios ni para errores de negocio que
 * exigen decidir algo: esos siguen junto al formulario/página. Un toast
 * puede ACOMPAÑAR a ese mensaje, nunca sustituirlo.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

export type TipoToast = 'success' | 'info' | 'warning' | 'error';

export interface OpcionesToast {
  readonly tipo?: TipoToast;
  /** ms; por defecto 4000 (8000 para error). 0 = no se cierra solo. */
  readonly duracion?: number;
}

interface Toast {
  readonly id: number;
  readonly mensaje: string;
  readonly tipo: TipoToast;
  readonly duracion: number;
}

export const MAX_TOASTS = 3;
export const DURACION_TOAST: Readonly<Record<TipoToast, number>> = { success: 4000, info: 4000, warning: 6000, error: 8000 };
const TITULO: Readonly<Record<TipoToast, string>> = { success: 'Hecho', info: 'Información', warning: 'Atención', error: 'Error' };
const ICONO: Readonly<Record<TipoToast, string>> = { success: '✓', info: 'i', warning: '!', error: '×' };

export interface ApiToast {
  mostrar: (mensaje: string, opciones?: OpcionesToast) => void;
  exito: (mensaje: string) => void;
  error: (mensaje: string) => void;
  info: (mensaje: string) => void;
  aviso: (mensaje: string) => void;
}

const NADA: ApiToast = { mostrar: () => {}, exito: () => {}, error: () => {}, info: () => {}, aviso: () => {} };
const Contexto = createContext<ApiToast>(NADA);

/** Fuera de un `ToastProvider` (pruebas de componente sueltas) es un no-op: nunca revienta. */
export function useToast(): ApiToast {
  return useContext(Contexto);
}

function ToastItem({ t, cerrar }: { t: Toast; cerrar: (id: number) => void }): ReactNode {
  useEffect(() => {
    if (t.duracion <= 0) return undefined;
    const temporizador = setTimeout(() => cerrar(t.id), t.duracion);
    return () => clearTimeout(temporizador);
  }, [t, cerrar]);
  return (
    <div className={`toast toast-${t.tipo}`} data-tipo={t.tipo}>
      <span className="toast-icono" aria-hidden="true">{ICONO[t.tipo]}</span>
      <span className="toast-mensaje"><span className="sr-solo">{TITULO[t.tipo]}: </span>{t.mensaje}</span>
      <button type="button" className="toast-cerrar fantasma" aria-label="Cerrar aviso" onClick={() => cerrar(t.id)}>×</button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const siguiente = useRef(1);

  const cerrar = useCallback((id: number) => setToasts((xs) => xs.filter((x) => x.id !== id)), []);
  const mostrar = useCallback((mensaje: string, opciones: OpcionesToast = {}) => {
    const tipo = opciones.tipo ?? 'success';
    const t: Toast = { id: siguiente.current++, mensaje, tipo, duracion: opciones.duracion ?? DURACION_TOAST[tipo] };
    //  Un mensaje idéntico ya visible no se apila otra vez.
    setToasts((xs) => (xs.some((x) => x.mensaje === mensaje && x.tipo === tipo) ? xs : [...xs, t].slice(-MAX_TOASTS)));
  }, []);
  const api = useMemo<ApiToast>(() => ({
    mostrar,
    exito: (m) => mostrar(m, { tipo: 'success' }),
    error: (m) => mostrar(m, { tipo: 'error' }),
    info: (m) => mostrar(m, { tipo: 'info' }),
    aviso: (m) => mostrar(m, { tipo: 'warning' }),
  }), [mostrar]);

  const tranquilos = toasts.filter((t) => t.tipo === 'success' || t.tipo === 'info');
  const urgentes = toasts.filter((t) => t.tipo === 'warning' || t.tipo === 'error');
  return (
    <Contexto.Provider value={api}>
      {children}
      {/* Regiones vivas SIN rol status/alert: los mensajes de error y estado de cada página siguen siendo
          los únicos `role="alert"`/`role="status"` de la pantalla (no compiten con el aviso contextual). */}
      <div className="toasts">
        <div className="toasts-lista toasts-polite" aria-live="polite" aria-relevant="additions">
          {tranquilos.map((t) => <ToastItem key={t.id} t={t} cerrar={cerrar} />)}
        </div>
        <div className="toasts-lista toasts-assertive" aria-live="assertive" aria-relevant="additions">
          {urgentes.map((t) => <ToastItem key={t.id} t={t} cerrar={cerrar} />)}
        </div>
      </div>
    </Contexto.Provider>
  );
}
